import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';

// Context
import { AuthProvider, useAuth } from './context/AuthContext';
import { ThemeProvider } from './context/ThemeContext';

// Components
import Login from './components/Auth/Login';
import Register from './components/Auth/Register';
import { AdminRoute, CustomerRoute, ViewerRoute } from './components/Auth/ProtectedRoute';
import Dashboard from './components/Dashboard/Dashboard';
import AgentManagement from './components/Agents/AgentManagement';
import ConnectorManagement from './components/Connectors/ConnectorManagement';
import Settings from './components/Settings/Settings';
import EnhancedThreadsList from './components/Threads/EnhancedThreadsList';
import UserManagement from './components/UserManagement/UserManagement';
import Unauthorized from './components/Auth/Unauthorized';
import AgentSettingsPage from './components/AgentSettings/AgentSettingsPage';
import AgentDashboard from './components/Agents/AgentDashboard';
import AgentComponent from './components/Agents/AgentComponent';
import ToolsManagement from './components/Tools/ToolsManagement';
import ChatPlayground from './components/Chat/ChatPlayground';
import SankalvaXChat from './components/Chat/SankalvaXChat';
import WorkflowsV2 from './components/Workflows/WorkflowsV2';
import WorkflowBuilderV2 from './components/Workflows/WorkflowBuilderV2';
import TaskManagement from './components/Tasks/TaskManagement';
import DebugDashboard from './components/Debug/DebugDashboard';
import ReportsLayout from './components/Reports/ReportsLayout';
import CronLogs from './components/CronJobs/CronLogs';
import WorkflowV2ChatPage from './components/V2Chat/WorkflowV2ChatPage';
import BankAgentChat from './components/BankAgent/BankAgentChat';
import BankDebugLogs from './components/BankAgent/BankDebugLogs';
import BankChatHistory from './components/BankAgent/BankChatHistory';
import SaleOrderAgentChat from './components/SaleOrderAgent/SaleOrderAgentChat';
import SaleDebugDashboard from './components/SaleOrderAgent/SaleDebugDashboard';
import BillProcessingHistory from './components/BillProcessing/components/BillProcessingHistory';
import SpendAnalysis from './components/SpendAnalysis/SpendAnalysis';
import ImpactDashboard from './components/ImpactDashboard/ImpactDashboard';
import EmailRouterList from './components/EmailRouter/EmailRouterList';
import EmailRouterForm from './components/EmailRouter/EmailRouterForm';
import EmailRouterHistory from './components/EmailRouter/EmailRouterHistory';

const AppRoutes = () => {
  const { user, isLoading } = useAuth();
  const isAuthenticated = !!user;
  
  console.log('🎯 [DEBUG] AppRoutes - isAuthenticated:', isAuthenticated, 'user:', user, 'isLoading:', isLoading);

  if (isLoading) {
    console.log('⏳ [DEBUG] AppRoutes - Showing loading screen');
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="spinner"></div>
        <span className="ml-4 text-lg">Loading SankalvaX Platform...</span>
      </div>
    );
  }

  return (
    <Routes>
      {/* Public Routes */}
      <Route 
        path="/login" 
        element={
          isAuthenticated ? <Navigate to="/dashboard" replace /> : <Login />
        } 
      />
      <Route 
        path="/register" 
        element={
          isAuthenticated ? <Navigate to="/dashboard" replace /> : <Register />
        } 
      />
      
      {/* Protected Routes - All authenticated users */}
      <Route 
        path="/dashboard" 
        element={
          isAuthenticated ? <Dashboard /> : <Navigate to="/login" replace />
        } 
      />
      
      {/* Admin-only Routes */}
      <Route 
        path="/users" 
        element={
          <AdminRoute>
            <UserManagement />
          </AdminRoute>
        } 
      />
      <Route
        path="/connectors"
        element={
          <CustomerRoute>
            <ConnectorManagement />
          </CustomerRoute>
        }
      />
      <Route 
        path="/tools" 
        element={
          <AdminRoute>
            <ToolsManagement />
          </AdminRoute>
        } 
      />
      
      {/* Customer and Admin Routes */}
      <Route 
        path="/agents" 
        element={
          <CustomerRoute>
            <AgentManagement />
          </CustomerRoute>
        } 
      />
      
      {/* Workflows - Customer and Admin Routes (New V2 System) */}
      <Route 
        path="/workflows" 
        element={
          <CustomerRoute>
            <WorkflowsV2 />
          </CustomerRoute>
        } 
      />
      <Route
        path="/workflows/builder"
        element={
          <CustomerRoute>
            <WorkflowBuilderV2 />
          </CustomerRoute>
        }
      />

      {/* V2 Workflow Chat - Customer and Admin Routes */}
      <Route
        path="/workflows/:id/chat"
        element={
          <CustomerRoute>
            <WorkflowV2ChatPage />
          </CustomerRoute>
        }
      />
      
      {/* Debug Dashboard - Admin Route */}
      <Route 
        path="/debug" 
        element={
          <AdminRoute>
            <DebugDashboard />
          </AdminRoute>
        } 
      />
      
      {/* Task Management - Customer and Admin Routes */}
      <Route 
        path="/tasks" 
        element={
          <CustomerRoute>
            <TaskManagement />
          </CustomerRoute>
        } 
      />
      
      {/* Reports - Customer and Admin Routes */}
      <Route 
        path="/reports" 
        element={
          <CustomerRoute>
            <ReportsLayout />
          </CustomerRoute>
        } 
      />
      <Route 
        path="/reports/:category" 
        element={
          <CustomerRoute>
            <ReportsLayout />
          </CustomerRoute>
        } 
      />
      <Route 
        path="/reports/:category/:reportName" 
        element={
          <CustomerRoute>
            <ReportsLayout />
          </CustomerRoute>
        } 
      />
      
      {/* Cron Jobs - Customer and Admin Routes */}
      <Route 
        path="/cron-jobs" 
        element={
          <CustomerRoute>
            <CronLogs />
          </CustomerRoute>
        } 
      />
      
      {/* Agent Dashboard - for all authenticated users */}
      <Route
        path="/agents/:agentId"
        element={
          isAuthenticated ? <AgentDashboard /> : <Navigate to="/login" replace />
        }
      />

      {/* Agent Full-Page Component - for all authenticated users */}
      <Route 
        path="/agents/:agentId/component/:componentName" 
        element={
          isAuthenticated ? <AgentComponent /> : <Navigate to="/login" replace />
        } 
      />
      
      {/* Admin-only Agent Settings */}
      <Route 
        path="/agents/:agentId/settings" 
        element={
          <AdminRoute>
            <AgentSettingsPage />
          </AdminRoute>
        } 
      />
      
      {/* All authenticated users */}
      <Route 
        path="/chat-playground" 
        element={
          isAuthenticated ? <ChatPlayground /> : <Navigate to="/login" replace />
        } 
      />
      <Route 
        path="/chat" 
        element={
          isAuthenticated ? <SankalvaXChat /> : <Navigate to="/login" replace />
        } 
      />
      <Route
        path="/chat/:sessionId"
        element={
          isAuthenticated ? <SankalvaXChat /> : <Navigate to="/login" replace />
        }
      />
      <Route
        path="/bank-agent-chat"
        element={
          isAuthenticated ? <BankAgentChat /> : <Navigate to="/login" replace />
        }
      />
      <Route
        path="/bank-debug"
        element={
          isAuthenticated ? <BankDebugLogs /> : <Navigate to="/login" replace />
        }
      />
      <Route
        path="/bank-chat-history"
        element={
          isAuthenticated ? <BankChatHistory /> : <Navigate to="/login" replace />
        }
      />
      <Route
        path="/bill-info"
        element={
          <AdminRoute>
            <BillProcessingHistory />
          </AdminRoute>
        }
      />
      <Route
        path="/spend-analysis/:agentId"
        element={
          <AdminRoute>
            <SpendAnalysis />
          </AdminRoute>
        }
      />
      <Route
        path="/spend-analysis"
        element={
          <AdminRoute>
            <SpendAnalysis agentId={3} agentName="Bill Processing" />
          </AdminRoute>
        }
      />
      <Route
        path="/impact-dashboard/:agentId"
        element={
          isAuthenticated ? <ImpactDashboard /> : <Navigate to="/login" replace />
        }
      />
      <Route
        path="/impact-dashboard"
        element={
          isAuthenticated ? <ImpactDashboard agentId={3} agentName="Bill Agent" /> : <Navigate to="/login" replace />
        }
      />
      <Route
        path="/email-router"
        element={isAuthenticated ? <EmailRouterList /> : <Navigate to="/login" replace />}
      />
      <Route
        path="/email-router/new"
        element={isAuthenticated ? <EmailRouterForm /> : <Navigate to="/login" replace />}
      />
      <Route
        path="/email-router/edit/:id"
        element={isAuthenticated ? <EmailRouterForm /> : <Navigate to="/login" replace />}
      />
      <Route
        path="/email-router/history"
        element={isAuthenticated ? <EmailRouterHistory /> : <Navigate to="/login" replace />}
      />
      <Route
        path="/sale-order-agent"
        element={
          isAuthenticated ? <SaleOrderAgentChat /> : <Navigate to="/login" replace />
        }
      />
      <Route
        path="/sale-debug"
        element={
          isAuthenticated ? <SaleDebugDashboard /> : <Navigate to="/login" replace />
        }
      />
      <Route
        path="/threads"
        element={
          isAuthenticated ? <EnhancedThreadsList /> : <Navigate to="/login" replace />
        }
      />
      <Route 
        path="/settings" 
        element={
          isAuthenticated ? <Settings /> : <Navigate to="/login" replace />
        } 
      />
      
      {/* Unauthorized page */}
      <Route 
        path="/unauthorized" 
        element={<Unauthorized />} 
      />
      
      {/* Default Route */}
      <Route 
        path="/" 
        element={
          <Navigate to={isAuthenticated ? "/dashboard" : "/login"} replace />
        } 
      />
      
      {/* Catch all route */}
      <Route 
        path="*" 
        element={
          <Navigate to={isAuthenticated ? "/dashboard" : "/login"} replace />
        } 
      />
    </Routes>
  );
};

function App() {
  console.log('🎬 [DEBUG] App.js - Component loaded');

  return (
    <ThemeProvider>
      <AuthProvider>
        <Router>
          <div className="App">
            <Toaster 
              position="top-right"
              toastOptions={{
                duration: 4000,
                style: {
                  background: 'var(--card-bg-light)',
                  color: 'var(--text-primary-light)',
                  border: '1px solid var(--border-light)',
                },
                success: {
                  iconTheme: {
                    primary: 'var(--secondary-color)',
                    secondary: 'white',
                  },
                },
                error: {
                  iconTheme: {
                    primary: 'var(--accent-color)',
                    secondary: 'white',
                  },
                },
              }}
            />
            
            <AppRoutes />
          </div>
        </Router>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;